/**
 * Supabase client for the portal SPA.
 *
 * One client instance per browser session. Uses the anon key only (TECH-08,
 * ARCH-002 §4). The service-role key is intentionally not referenced here; it
 * lives only in `packages/studio-mcp` per the boundary fixed by ARCH-002.
 *
 * Sessions persist in localStorage by default (`supabase-js` default).
 * Refresh-token rotation is on in `supabase/config.toml` so sessions refresh
 * transparently up to the rotation TTL (30 days for subscribers, ARCH-002 §7).
 * Operator session shortening (7 days) is enforced application-side in
 * `lib/auth.ts` by checking JWT issued_at against a 7-day window.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { createLogger } from '@studio/logger';

const supabaseLogger = createLogger('portal');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fail loud during dev. Production builds without these env vars will fail
  // at the first request anyway; surfacing it on module load makes the cause
  // obvious in the browser console rather than buried in a network panel.
  supabaseLogger.error(
    { surface: 'portal' },
    '[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Auth and data calls will fail.',
  );
}

export const supabase: SupabaseClient = createClient(
  supabaseUrl ?? 'http://127.0.0.1:54321',
  supabaseAnonKey ?? 'anon-key-missing',
  {
    auth: {
      // Default behaviors made explicit so a future change in supabase-js
      // defaults does not silently shift the contract.
      autoRefreshToken: true,
      persistSession: true,
      // MUST stay false. PKCE codes (and the code_verifier in storage) are
      // one-time-use. With this on, supabase-js auto-detects the `?code=` on
      // the `/auth/callback` URL and exchanges it on client init — racing the
      // explicit `exchangeCodeForSession` call in routes/auth/callback.tsx.
      // The auto-exchange usually wins (it fires on module import, before the
      // route's effect), establishing the session but consuming the code, so
      // the manual exchange then fails with "expired or already used" and
      // renders the failure page even though sign-in actually succeeded.
      // The callback route is the single, explicit owner of the exchange.
      detectSessionInUrl: false,
      // PKCE flow is the magic-link path. Supabase requires it for the
      // `auth/callback` exchange to work via `exchangeCodeForSession`.
      flowType: 'pkce',
      storageKey: 'studio-subscription-auth',
    },
  },
);
